import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, RefreshCw, ShieldCheck } from 'lucide-react';
import { api } from '../lib/api';
import { customerProfiles } from '../lib/customer-profiles';
import CustomerProfilesManagement from '../components/CustomerProfilesManagement';

// Read the SAME Customer360 record and show the SAME profile management component;
// there is no second Profile authority, bypass token, or customer-data copy.
export default function ProfilesCustomerRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const requestSequence = useRef(0);

  const load = useCallback(async () => {
    const request = ++requestSequence.current;
    setLoading(true);
    setError('');
    try {
      const response = await api('/api/customers/' + encodeURIComponent(id));
      if (!response?.customer || typeof response.customer !== 'object' || Array.isArray(response.customer)) {
        throw new Error('Customer360 did not return a valid customer record.');
      }
      if (request !== requestSequence.current) return;
      setRecord(response);
    } catch (failure) {
      if (request !== requestSequence.current) return;
      setError(failure.message || 'Customer360 is unavailable or access was denied.');
      setRecord(null);
    } finally {
      if (request === requestSequence.current) setLoading(false);
    }
  }, [id]);
  useEffect(() => {
    void load();
    return () => { requestSequence.current += 1; };
  }, [load]);

  const profiles = record ? customerProfiles(record) : [];
  const customer = record?.customer || {};
  const reference = customer.customer_number || customer.id || id;

  return <main className="profiles-admin-workspace profiles-admin-record">
    <nav className="profiles-review-breadcrumb" aria-label="Profiles administration navigation">
      <Link to="/profiles/admin"><ArrowLeft size={16} aria-hidden="true" />Profiles administration</Link>
      <span aria-hidden="true">/</span><span>Customer Profiles</span>
    </nav>
    <header className="profiles-admin-workspace-header">
      <div>
        <span className="profiles-admin-workspace-kicker">SM-CUSTOMER-OPERATIONS / CUSTOMER360</span>
        <h1 className="govuk-heading-xl">{record ? customer.display_name || reference : 'Profiles customer record'}</h1>
        <p className="govuk-body">Authoritative customer-linked Profile IDs, current publication status and governed staff actions. All referrals use the existing CRM Back Office approval workflow.</p>
      </div>
      <button type="button" className="button secondary govuk-button govuk-button--secondary" disabled={loading} onClick={() => void load()}><RefreshCw size={15} />Refresh record</button>
    </header>
    {loading ? <section className="profiles-admin-panel"><p className="profiles-admin-empty govuk-body" role="status">Loading the authorised Customer360 record…</p></section> :
      error ? <section className="profiles-admin-panel"><p role="alert" className="profiles-admin-error govuk-body">{error}</p><p className="profiles-admin-empty govuk-body">No cached Profile details are displayed after a failed or unauthorised request.</p></section> :
      <>
        <section className="profiles-admin-panel">
          <div className="profiles-admin-panel-heading">
            <div><span>CUSTOMER ACCOUNT</span><h2 className="govuk-heading-m">{reference}</h2><p className="govuk-body">{customer.verified_email || 'No verified email available'} · {profiles.length} linked Profile{profiles.length === 1 ? '' : 's'}</p></div>
            <Link to={'/customers/' + encodeURIComponent(id) + '#services'} className="button secondary govuk-button govuk-button--secondary">Open full Customer360 <ArrowUpRight size={15} /></Link>
          </div>
          <p className="profiles-admin-empty govuk-body">Profile data is read directly from the existing authorised customer service snapshots. No separate Profiles administrator login or database is used.</p>
        </section>
        <section className="profiles-admin-panel profiles-admin-c360-control" aria-label="Profiles management and governed actions">
          <CustomerProfilesManagement key={id} profiles={profiles} customerId={customer.id || id} onBackOffice={() => navigate('/back-office')} />
        </section>
        <p className="profiles-admin-legacy-notice govuk-body"><ShieldCheck size={16} aria-hidden="true" />Verification approvals, profile changes and customer disputes remain subject to the existing CRM permissions, evidence review and Back Office decisions. The legacy Admin Centre remains available until complete functional and visual parity is signed off.</p>
      </>}
  </main>;
}
